// src/collab/useCollaboration.js
// Who runs a tournament with whom: `role` ('owner' or 'coorganizer'), `shared` and `coOrganizerCount`. The server
// tells (the app's own answers already carry it); a device in offline mode has no idea of other people, so it asks the
// server when it can, and remembers the answer for when it cannot.
import { useCallback, useEffect, useState } from 'react';
import { localEngineEnabled } from '../engine/engineFlag';
import { serverClient } from '../sync/sync';
import { useDataChanged } from '../testMode/testMode';

const cacheKey = (userId) => `tournamental.collab.${userId}`;

function readCache(userId) {
    try {
        return JSON.parse(window.localStorage.getItem(cacheKey(userId))) || {};
    } catch (error) {
        return {};
    }
}

const PLAIN = { role: 'owner', shared: false, coOrganizerCount: 0 };

export function useCollaboration(user) {
    const userId = user?.uid;
    const [known, setKnown] = useState(() => (userId ? readCache(userId) : {}));

    const refresh = useCallback(async () => {
        if (!userId || user.isGuest || !localEngineEnabled()) return;
        try {
            const list = await serverClient(userId)('GET', '/getTournaments');
            const next = Object.fromEntries(
                list.map((t) => [t.tournamentId, { role: t.role, shared: t.shared, coOrganizerCount: t.coOrganizerCount }])
            );
            setKnown(next);
            try {
                window.localStorage.setItem(cacheKey(userId), JSON.stringify(next));
            } catch (error) {
                // not remembered
            }
        } catch (error) {
            setKnown(readCache(userId)); // no connection: what was known last time
        }
    }, [userId, user]);

    useEffect(() => {
        refresh();
        window.addEventListener('online', refresh);
        return () => window.removeEventListener('online', refresh);
    }, [refresh]);
    useDataChanged(refresh);

    // `tournament` is what the app knows about it (a list entry); a device in offline mode adds what the server said.
    return useCallback(
        (tournament) => {
            if (!tournament) return PLAIN;
            if (localEngineEnabled() && known[tournament.tournamentId]) return { ...PLAIN, ...known[tournament.tournamentId] };
            return {
                role: tournament.role || 'owner',
                shared: Boolean(tournament.shared),
                coOrganizerCount: tournament.coOrganizerCount || 0,
            };
        },
        [known]
    );
}
